import { useState, useRef, useEffect } from "react";
import { askBrain, findByName, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { cloudBackupStatus } from "../brain";
import { executeAction, type ChatAction, type FilterRow } from "./actions";
import CallOverlay from "../live/CallOverlay";

// Unique per-app keys so this chat never collides with another Oriora app's transcript.
// The HOME chat (collectionId === null — manage collections) keeps its own transcript,
// separate from the in-collection chat.
const COLL_KEY = "oriora.oriora_curio.chat.v1";
const HOME_KEY = "oriora.oriora_curio.homechat.v1";
const storeKeyFor = (collectionId: string | null) => (collectionId ? COLL_KEY : HOME_KEY);
const greetingFor = (collectionId: string | null): ChatTurn => ({
  role: "assistant",
  content: collectionId
    ? "Tell me what to do — add several items at once, research a piece, move something to another collection, or summarize this collection. Just say it."
    : "Tell me what to do — start a new collection, get ideas for what to collect, move items between collections, or delete one. Open a collection to work inside it. Just say it.",
});

function loadChat(key: string, greeting: ChatTurn): ChatTurn[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length) return p;
    }
  } catch {}
  return [greeting];
}
function saveChat(key: string, turns: ChatTurn[]) {
  try {
    localStorage.setItem(key, JSON.stringify(turns.slice(-100)));
  } catch {}
}

export default function Chat({ collectionId, onChanged }: { collectionId: string | null; onChanged?: () => void }) {
  const storeKey = storeKeyFor(collectionId);
  const greeting = greetingFor(collectionId);
  const [turns, setTurns] = useState<ChatTurn[]>(() => loadChat(storeKey, greeting));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  const [pending, setPending] = useState<ChatAction | null>(null);
  // A photo picked in the composer, waiting to be filed onto a collection item. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy, pending]);
  useEffect(() => {
    saveChat(storeKey, turns);
  }, [turns, storeKey]);

  // A photo turn is filed ON-DEVICE onto a collection item (its catalogue photo): no model call, no
  // cost. The user names the item in the same message; fuzzy name-resolution finds the closest one.
  // Home chat (collectionId === null) resolves across all collections.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "(photo)" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      const q = text.trim();
      if (!q) {
        setTurns([...next, { role: "assistant", content: "Which item is this photo for? Name it, then send." }]);
        return; // keep the photo attached so the next message can name it
      }
      const matches = await findByName(collectionId, q);
      if (matches.length === 0) {
        setTurns([...next, { role: "assistant", content: `I couldn't find an item matching "${q}". Add it first, then I'll attach the photo.` }]);
        return; // keep the photo attached
      }
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((it) => `• ${it.name}`).join("\n");
        setTurns([...next, { role: "assistant", content: `More than one item matches — which one?\n${list}` }]);
        return; // keep the photo attached
      }
      const target = matches[0];
      await store.updateItem(target.id, { photo_ref: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `Added your photo to ${target.name}.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next);
    setInput("");
    setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, collectionId, onChunk);
      const transcript = next.map((t) => `${t.role}: ${t.content}`).join("\n");
      const needsConfirm = (a: ChatAction) => a.kind === "backup_restore" || a.kind === "import_file" || a.kind === "delete_item" || a.kind === "delete_collection" ||
        a.kind === "delete_set" || a.kind === "delete_provenance" ||
        a.kind === "library_remove" || a.kind === "library_merge";
      const warnFor = (a: ChatAction) =>
        
  a.kind === "library_merge"
    ? `Merge ${a.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : a.kind === "library_remove"
      ? `Remove ${a.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : a.kind === "import_file" ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
        : a.kind === "backup_restore" ? `Restore will REPLACE everything in the app with your ${a.provider === "gdrive" ? "Google Drive" : a.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
        : a.kind === "delete_collection" ? `Delete the "${(a as { label?: string }).label ?? "collection"}" collection and everything in it? Tap Confirm, or Cancel.`
        : a.kind === "delete_set" ? `Delete ${(a as { label?: string }).label ?? "this set"}? Items in it stay, just ungrouped. Tap Confirm, or Cancel.`
        : `Delete ${(a as { label?: string }).label ?? "this item"}? Tap Confirm, or Cancel.`;

      // Execute every NON-confirm action the brain returned, in order (multiple items in one message
      // → one action each). A destructive action is pended for a Confirm tap after the safe ones run.
      const msgs: string[] = [];
      let changed = false;
      let rows: FilterRow[] | undefined;
      for (const action of actions) {
        if (needsConfirm(action)) continue;
        const result = await executeAction(action, collectionId, { transcript });
        if (result.message) msgs.push(result.message);
        if (result.changed) changed = true;
        if (result.rows) rows = result.rows;
      }
      if (changed) onChanged?.();

      let confirmAction = actions.find(needsConfirm);
      if (confirmAction) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        if (confirmAction.kind === "backup_restore" && !confirmAction.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) confirmAction = { ...confirmAction, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        setPending(confirmAction);
        setTurns([...next, { role: "assistant", content: [reply, ...msgs, warnFor(confirmAction)].filter(Boolean).join("\n\n") }]);
        setBusy(false);
        return;
      }
      setTurns([...next, { role: "assistant", content: [reply, ...msgs].filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      const result = await executeAction(pending, collectionId);
      setTurns((t) => [...t, { role: "assistant", content: result.message || "Done." }]);
      if (result.changed) onChanged?.();
    } catch (e) {
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setPending(null);
      setBusy(false);
    }
  }
  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([greeting]);
    try { localStorage.removeItem(storeKey); } catch { /* ignore */ }
  }

  function cancelPending() {
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." }]);
  }

  return (
    <div className="ocu-chat">
      <div className="ocu-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`ocu-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`ocu-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
              {t.role !== "user" && t.content.trim() ? (
                <div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>
              ) : null}
            </div>
          </div>
        ))}
        {busy && (
          <div className="ocu-msgrow">
            <div className="ocu-bubble bot thinking">thinking</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="ocu-composer ocu-confirm">
          <button
            className="btn btn-primary"
            style={{ background: "var(--rose, #b3503a)", borderColor: "var(--rose, #b3503a)" }}
            onClick={confirmPending}
            disabled={busy}
          >
            Confirm delete
          </button>
          <button className="btn" onClick={cancelPending} disabled={busy}>
            Cancel
          </button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="ocu-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the item…" : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position
                sheet, so it renders correctly anywhere in the tree, and this needs no
                structural analysis of each app's JSX — a button whose overlay never got
                mounted is the half-wired failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="ocu-rows">
      {rows.map((r) => (
        <div key={r.id} className="ocu-rowitem">
          <span>{r.name}</span>
          <span className="tnum">{r.value !== "—" ? r.value : r.status}</span>
        </div>
      ))}
    </div>
  );
}
